import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail, Lock, User, ArrowRight, Shield, Users, UserCheck,
  ChevronDown, Church, CheckCircle, ExternalLink,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCongregation } from '../contexts/CongregationContext';

const IS_DEV = import.meta.env.DEV;

const DEMO_ACCOUNTS = [
  { label: 'Beheerder',       description: 'Volledige toegang',        email: 'demo1@test.com', password: 'DemoKerkPlanner', icon: Shield    },
  { label: 'Groepsbeheerder', description: 'Team & planning beheer',   email: 'demo2@test.com', password: 'DemoKerkPlanner', icon: Users     },
  { label: 'Vrijwilliger',    description: 'Rooster & beschikbaarheid',email: 'demo3@test.com', password: 'DemoKerkPlanner', icon: UserCheck },
];

const INPUT: React.CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.06)',
  borderColor: 'rgba(255,255,255,0.12)',
  color: '#FFFFFF',
  outline: 'none',
  borderRadius: '12px',
  fontSize: '14px',
};

export default function Login() {
  const { signIn, signUp } = useAuth();
  const { congregations, active, setActive } = useCongregation();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isSignUp) {
        const { error } = await signUp(email, password, fullName);
        if (error) setError(error.message);
        else { setSignUpSuccess(true); return; }
      } else {
        const { error } = await signIn(email, password);
        if (error) setError(error.message);
      }
    } catch {
      setError('Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  }

  async function handleDemoLogin(account: typeof DEMO_ACCOUNTS[number]) {
    setError('');
    setDemoLoading(account.email);
    const demo = congregations.find((c) => c.short_name === 'Demo');
    if (demo) setActive(demo);
    try {
      const { error } = await signIn(account.email, account.password);
      if (error) setError(error.message);
    } catch {
      setError('Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      setDemoLoading(null);
    }
  }

  function focusInput(e: React.FocusEvent<HTMLInputElement>) {
    e.currentTarget.style.borderColor = '#84D2C5';
    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(132,210,197,0.20)';
    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
  }
  function blurInput(e: React.FocusEvent<HTMLInputElement>) {
    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)';
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
  }

  /* ── Branding panel (shared between mobile/desktop) ──────────────── */
  const renderBrandPanel = (large?: boolean) => (
    <div className={`flex flex-col items-center ${large ? 'items-start text-left' : 'text-center'}`}>
      {/* Logo */}
      <div className="relative mb-6">
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(212,175,55,0.35) 0%, transparent 70%)',
            filter: 'blur(24px)',
            transform: 'scale(1.5)',
          }}
        />
        <img
          src="/Kerkplanner_logo.png"
          alt="Kerkplanner"
          style={{
            width: large ? 160 : 112,
            height: large ? 160 : 112,
            display: 'block',
            position: 'relative',
            filter: 'drop-shadow(0 8px 28px rgba(0,0,0,0.55))',
            transition: 'width 0.2s, height 0.2s',
          }}
        />
      </div>

      <h1
        className={`font-bold tracking-tight leading-none ${large ? 'text-4xl xl:text-5xl' : 'text-3xl'}`}
        style={{ color: '#FFFFFF', fontFamily: 'Inter, system-ui, sans-serif' }}
      >
        kerkplanner<span style={{ color: '#D4AF37' }}>.app</span>
      </h1>
      <p className={`mt-2 text-sm ${large ? 'mt-3 text-base' : ''}`} style={{ color: 'rgba(163,228,215,0.60)' }}>
        Schedule. Volunteer. Connect.
      </p>

      {large && (
        <div className="mt-8 space-y-3 max-w-xs">
          {[
            'Roosterbeheer voor vrijwilligers',
            'Beschikbaarheid & ruilverzoeken',
            'Teams, planning & meldingen',
          ].map((f) => (
            <div key={f} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#84D2C5' }} />
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>{f}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /* ── Form card ────────────────────────────────────────────────────── */
  const formCard = (
    <div
      className="rounded-2xl border w-full"
      style={{
        backgroundColor: '#13243A',
        borderColor: 'rgba(255,255,255,0.08)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
        padding: 'clamp(20px, 5vw, 32px)',
      }}
    >
      {signUpSuccess ? (
        <div className="text-center py-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: 'rgba(132,210,197,0.15)' }}>
            <CheckCircle className="w-8 h-8" style={{ color: '#84D2C5' }} />
          </div>
          <h2 className="text-xl font-bold mb-2 text-white">Controleer je e-mail</h2>
          <p className="mb-1 text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>
            We hebben een bevestigingslink gestuurd naar:
          </p>
          <p className="font-semibold mb-4 text-white">{email}</p>
          <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Klik op de link in de e-mail om je account te activeren. Check ook je spam-map.
          </p>
          <button type="button" onClick={() => { setSignUpSuccess(false); setIsSignUp(false); setPassword(''); }}
            className="text-sm font-medium hover:underline" style={{ color: '#84D2C5' }}>
            Terug naar inloggen
          </button>
        </div>
      ) : (
        <>
          {/* Tab switcher */}
          <div className="flex mb-6 rounded-xl p-1"
            style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {[{ label: 'Inloggen', val: false }, { label: 'Registreren', val: true }].map(({ label, val }) => (
              <button key={label} type="button" onClick={() => setIsSignUp(val)}
                className="flex-1 py-2 text-sm font-medium rounded-lg transition-all"
                style={isSignUp === val
                  ? { backgroundColor: '#1A2F4A', color: '#FFFFFF', boxShadow: '0 1px 4px rgba(0,0,0,0.25)' }
                  : { color: 'rgba(255,255,255,0.45)' }}>
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignUp && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5"
                  style={{ color: 'rgba(255,255,255,0.45)' }}>Naam</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(255,255,255,0.3)' }} />
                  <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
                    placeholder="Je volledige naam" required={isSignUp}
                    className="w-full pl-9 pr-4 py-2.5 border text-sm transition-all placeholder:text-[rgba(255,255,255,0.25)]"
                    style={INPUT} onFocus={focusInput} onBlur={blurInput} />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5"
                style={{ color: 'rgba(255,255,255,0.45)' }}>E-mailadres</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(255,255,255,0.3)' }} />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="naam@voorbeeld.nl" required
                  className="w-full pl-9 pr-4 py-2.5 border text-sm transition-all placeholder:text-[rgba(255,255,255,0.25)]"
                  style={INPUT} onFocus={focusInput} onBlur={blurInput} />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5"
                style={{ color: 'rgba(255,255,255,0.45)' }}>Wachtwoord</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(255,255,255,0.3)' }} />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimaal 6 tekens" required minLength={6}
                  className="w-full pl-9 pr-4 py-2.5 border text-sm transition-all placeholder:text-[rgba(255,255,255,0.25)]"
                  style={INPUT} onFocus={focusInput} onBlur={blurInput} />
              </div>
              {!isSignUp && (
                <div className="mt-1.5 text-right">
                  <Link to="/forgot-password" className="text-xs font-medium hover:underline" style={{ color: '#84D2C5' }}>
                    Wachtwoord vergeten?
                  </Link>
                </div>
              )}
            </div>

            {error && (
              <div className="p-3 rounded-xl border" style={{ borderColor: 'rgba(200,90,80,0.4)', backgroundColor: 'rgba(200,90,80,0.12)' }}>
                <p className="text-sm" style={{ color: '#f87171' }}>{error}</p>
              </div>
            )}

            <button type="submit" disabled={loading || demoLoading !== null}
              className="w-full flex items-center justify-center gap-2 py-3 font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#84D2C5', color: '#0D1B2A', boxShadow: '0 2px 12px rgba(132,210,197,0.35)' }}
              onMouseEnter={e => { if (!loading && !demoLoading) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#A3E4D7'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#84D2C5'; }}>
              {loading ? 'Even geduld...' : <>{isSignUp ? 'Account aanmaken' : 'Inloggen'}<ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          {!isSignUp && (IS_DEV || window.location.hostname === 'demo.kerkplanner.app') && (
            <div className="mt-6">
              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 text-xs font-semibold tracking-widest uppercase"
                    style={{ backgroundColor: '#13243A', color: 'rgba(255,255,255,0.3)' }}>
                    Demo accounts
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                {DEMO_ACCOUNTS.map((account) => {
                  const Icon = account.icon;
                  const isLoadingDemo = demoLoading === account.email;
                  return (
                    <button key={account.email} type="button" onClick={() => handleDemoLogin(account)}
                      disabled={loading || demoLoading !== null}
                      className="w-full flex items-center gap-3 px-4 py-3 border rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ borderColor: 'rgba(255,255,255,0.08)', color: '#FFFFFF', backgroundColor: 'rgba(255,255,255,0.03)' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.07)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.03)'; }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: 'rgba(132,210,197,0.15)', color: '#84D2C5' }}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-semibold text-white">{account.label}</p>
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{account.description}</p>
                      </div>
                      {isLoadingDemo && (
                        <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                          style={{ borderColor: '#84D2C5 transparent #84D2C5 #84D2C5' }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  /* ── Congregation picker (shared) ─────────────────────────────────── */
  const congPicker = congregations.length > 1 ? (
    <div className="relative flex justify-center" style={{ zIndex: 60 }}>
      <div className="relative">
        <button type="button" onClick={() => setShowPicker(!showPicker)}
          className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors border"
          style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}>
          <Church className="w-4 h-4" style={{ color: '#84D2C5' }} />
          <span>{active?.short_name || 'Kies gemeente'}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showPicker ? 'rotate-180' : ''}`} />
        </button>
        {showPicker && (
          <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 rounded-xl py-2 min-w-[220px] border"
            style={{ zIndex: 9999, backgroundColor: '#13243A', borderColor: 'rgba(255,255,255,0.10)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
            {congregations.map((c) => (
              <button key={c.id} type="button" onClick={() => { setActive(c); setShowPicker(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                style={{ color: '#FFFFFF' }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.06)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: c.color }}>
                  <Church className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium">{c.short_name}</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{c.city}</p>
                </div>
                {active?.id === c.id && (
                  <div className="ml-auto w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#84D2C5' }} />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  ) : null;

  /* ── Footer ───────────────────────────────────────────────────────── */
  const footer = (
    <div className="mt-5 flex items-center justify-center gap-4">
      <a href="https://joy-ict.nl/kerkplanner" target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-1.5 text-xs font-medium transition-colors hover:underline"
        style={{ color: 'rgba(163,228,215,0.55)' }}
        onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#84D2C5'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(163,228,215,0.55)'; }}>
        <ExternalLink className="w-3 h-3" />
        Over deze app
      </a>
      <span style={{ color: 'rgba(255,255,255,0.12)' }}>·</span>
      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>Made with care by Joy ICT</span>
    </div>
  );

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#0D1B2A' }}>

      {/* Ambient glows */}
      <div className="absolute pointer-events-none" style={{ width: 700, height: 700, top: '-15%', right: '-10%', background: 'radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 60%)', filter: 'blur(48px)' }} />
      <div className="absolute pointer-events-none" style={{ width: 600, height: 600, bottom: '-20%', left: '-10%', background: 'radial-gradient(circle, rgba(132,210,197,0.08) 0%, transparent 60%)', filter: 'blur(48px)' }} />

      {/* ── DESKTOP (lg+): two-column split ─────────────────────────── */}
      <div className="hidden lg:flex min-h-screen">

        {/* Left panel — branding */}
        <div className="flex-1 flex flex-col items-start justify-center px-16 xl:px-24 relative">
          {/* subtle vertical divider glow */}
          <div className="absolute right-0 top-0 bottom-0 w-px" style={{ background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.06) 30%, rgba(255,255,255,0.06) 70%, transparent)' }} />
          {renderBrandPanel(true)}
        </div>

        {/* Right panel — form */}
        <div className="w-[480px] xl:w-[520px] flex flex-col justify-center px-10 xl:px-14 py-10">
          {congPicker}
          <div className={congregations.length > 1 ? 'mt-4' : ''}>
            {formCard}
          </div>
          {footer}
        </div>
      </div>

      {/* ── TABLET + MOBILE (< lg): centered single column ───────────── */}
      <div className="lg:hidden flex flex-col items-center justify-center min-h-screen px-4 py-10 sm:py-14">
        <div className="w-full max-w-sm sm:max-w-md">

          {/* Branding */}
          <div className="text-center mb-6 sm:mb-8">
            {renderBrandPanel()}
          </div>

          {/* Congregation picker */}
          <div className="mb-4">
            {congPicker}
          </div>

          {formCard}
          {footer}
        </div>
      </div>

    </div>
  );
}
